import React, { useCallback, useEffect, useRef, useState } from 'react';
import ContentBox from '../ContentBox';
import { getStaffLog } from '../../services/api';

const REFRESH_MS = 120000;
const PAGE_SIZE = 30;

const countdownLabel = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const CATEGORY_TINTS = {
    framps: '#3f7d3f',
    achievements: '#a5762a',
    moderation: '#8c2f2f',
    shop: '#2f5f8c',
    core: '#5a2929',
    polls: '#5b3f8c',
    articles: '#2f7d7d',
    staffteam: '#8c5a2f',
    appreciation: '#8c2f6b',
    events: '#e8871e',
    uploads: '#4a4a4a'
};

function relativeTime(value) {
    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return '';

    const seconds = Math.round((Date.now() - then.getTime()) / 1000);
    if (seconds < 45) return 'just now';
    if (seconds < 90) return 'a minute ago';

    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minutes ago`;

    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

    const days = Math.round(hours / 24);
    if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;

    return then.toLocaleDateString();
}

function LogRow({ entry }) {
    const tint = CATEGORY_TINTS[entry.category] || '#4a4a4a';

    return (
        <li className="flex items-start gap-3 border-b border-gray-200 py-2 last:border-b-0">
            <span
                className="mt-[2px] shrink-0 rounded px-2 py-[2px] font-['Jersey_10'] text-[13px] uppercase leading-none tracking-[1px] text-white"
                style={{ backgroundColor: tint }}
            >
                {entry.category}
            </span>

            <div className="min-w-0 flex-1">
                <p className="break-words text-[14px] text-gray-800">
                    <span className="font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-maroon">
                        {entry.habboName}
                    </span>{' '}
                    {entry.summary}
                </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
                {entry.adminOnly && (
                    <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-red-600">
                        admin
                    </span>
                )}
                <span
                    className="whitespace-nowrap text-[12px] text-gray-500"
                    title={new Date(entry.createdAt).toLocaleString()}
                >
                    {relativeTime(entry.createdAt)}
                </span>
            </div>
        </li>
    );
}

function StaffLogFeed({ userData }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [deadline, setDeadline] = useState(() => Date.now() + REFRESH_MS);
    const [now, setNow] = useState(() => Date.now());

    const newestIdRef = useRef(0);
    const activeRef = useRef(true);
    const hasEntriesRef = useRef(false);

    useEffect(() => {
        activeRef.current = true;
        return () => { activeRef.current = false; };
    }, []);

    const applyNewest = useCallback((incoming) => {
        if (!incoming.length) return;
        newestIdRef.current = Math.max(newestIdRef.current, incoming[0].id);
    }, []);

    useEffect(() => {
        let active = true;

        const load = async () => {
            const res = await getStaffLog({ limit: PAGE_SIZE }).catch(() => null);
            if (!active) return;

            if (res?.success) {
                setEntries(res.entries || []);
                setHasMore(!!res.hasMore);
                applyNewest(res.entries || []);
                setError(null);
            } else {
                setError('Could not load the staff log.');
            }
            setLoading(false);
        };

        load();
        return () => { active = false; };
    }, [applyNewest]);

    const refresh = useCallback(async () => {
        setDeadline(Date.now() + REFRESH_MS);
        setRefreshing(true);

        const res = await getStaffLog({ limit: PAGE_SIZE, afterId: newestIdRef.current || null }).catch(() => null);

        if (!activeRef.current) return;
        setRefreshing(false);

        if (!res?.success) {
            if (!hasEntriesRef.current) setError('Could not load the staff log.');
            return;
        }

        setError(null);

        const fresh = res.entries || [];
        if (!fresh.length) return;

        applyNewest(fresh);
        setEntries(prev => {
            const seen = new Set(prev.map(e => e.id));
            return [...fresh.filter(e => !seen.has(e.id)), ...prev];
        });
    }, [applyNewest]);

    useEffect(() => {
        hasEntriesRef.current = entries.length > 0;
    }, [entries]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (now < deadline) return;
        refresh();
    }, [now, deadline, refresh]);

    const loadMore = async () => {
        const oldest = entries[entries.length - 1];
        if (!oldest) return;

        setLoadingMore(true);
        const res = await getStaffLog({ limit: PAGE_SIZE, beforeId: oldest.id }).catch(() => null);

        if (res?.success) {
            setEntries(prev => [...prev, ...(res.entries || [])]);
            setHasMore(!!res.hasMore);
        }
        setLoadingMore(false);
    };

    return (
        <ContentBox
            theme="default"
            tint="red"
            header={userData?.hasAdminRole ? 'Staff Log (all activity)' : 'Staff Log'}
            headerRight={
                <button
                    type="button"
                    onClick={refresh}
                    disabled={refreshing}
                    title="Refresh the staff log now"
                    className="flex items-center gap-1 rounded bg-black/25 px-2 py-[3px] font-['Jersey_10'] text-[14px] uppercase leading-none tracking-[1px] text-white transition-all hover:bg-black/40 disabled:opacity-60"
                >
                    <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                    <span className="tabular-nums opacity-80">
                        {refreshing ? '' : countdownLabel(deadline - now)}
                    </span>
                </button>
            }
        >
            {loading ? (
                <p className="text-gray-600">Loading the log...</p>
            ) : error ? (
                <p className="text-gray-600">{error}</p>
            ) : entries.length === 0 ? (
                <p className="text-gray-600">Nothing logged yet. Staff actions will appear here as they happen.</p>
            ) : (
                <>
                    <ul className="max-h-[260px] overflow-y-auto pr-1">
                        {entries.map(entry => (
                            <LogRow key={entry.id} entry={entry} />
                        ))}
                    </ul>

                    {hasMore && (
                        <button
                            type="button"
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="mt-3 w-full rounded bg-gray-200 px-3 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-800 transition-all hover:bg-gray-300 disabled:opacity-60"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                        >
                            {loadingMore ? 'Loading...' : 'Load older entries'}
                        </button>
                    )}
                </>
            )}
        </ContentBox>
    );
}

export default StaffLogFeed;
